import React from 'react';
import { Badge } from '../Badge';
import { SearchIcon, MailIcon, ChevronUpIcon, ChevronDownIcon, ChevronsUpDownIcon, ChevronRightIcon, CheckIcon, Settings2Icon, ExternalLinkIcon } from 'lucide-react';
import { TABLE_COLUMNS, getTypeBadgeVariant, getStatusBadgeVariant, DEFAULT_REVIEW_STATE, type ColumnId, type SortColumn, type SortDirection, type DisplayRow, type ReviewDocument, type DocReviewState, type CaseIssue } from './types';
import { CheckCircleIcon, TagIcon } from 'lucide-react';
interface DocumentTableProps {
  paginatedRows: DisplayRow[];
  gridTemplate: string;
  isColumnVisible: (id: ColumnId) => boolean;
  visibleColumns: Set<ColumnId>;
  columnDropdownOpen: boolean;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  selectedDoc: ReviewDocument | null;
  expandedThreads: Set<string>;
  totalFiltered: number;
  showingFrom: number;
  showingTo: number;
  totalPages: number;
  safeCurrentPage: number;
  pageSize: number;
  pageSizeDropdownOpen: boolean;
  handleSort: (col: SortColumn) => void;
  toggleThread: (threadId: string) => void;
  toggleColumn: (id: ColumnId) => void;
  setColumnDropdownOpen: (v: boolean) => void;
  setSelectedDoc: (doc: ReviewDocument | null) => void;
  setHoveredDoc: (doc: ReviewDocument | null) => void;
  handleRowMouseEnter: (doc: ReviewDocument, e: React.MouseEvent<HTMLDivElement>) => void;
  handleRowMouseLeave: () => void;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: (size: 25 | 50 | 100) => void;
  setPageSizeDropdownOpen: (v: boolean) => void;
  clearAllFilters: () => void;
  getThreadSize: (threadId: string) => number;
  columnDropdownRef: React.RefObject<HTMLDivElement | null>;
  pageSizeDropdownRef: React.RefObject<HTMLDivElement | null>;
  docStates: Record<string, DocReviewState>;
  caseIssues: CaseIssue[];
  docIssues: Record<string, string[]>;
  issueFilter: string | null;
  onIssueClick: (issueId: string) => void;
}
export function DocumentTable({
  paginatedRows,
  gridTemplate,
  isColumnVisible,
  visibleColumns,
  columnDropdownOpen,
  sortColumn,
  sortDirection,
  selectedDoc,
  expandedThreads,
  totalFiltered,
  showingFrom,
  showingTo,
  totalPages,
  safeCurrentPage,
  pageSize,
  pageSizeDropdownOpen,
  handleSort,
  toggleThread,
  toggleColumn,
  setColumnDropdownOpen,
  setSelectedDoc,
  setHoveredDoc,
  handleRowMouseEnter,
  handleRowMouseLeave,
  setCurrentPage,
  setPageSize,
  setPageSizeDropdownOpen,
  clearAllFilters,
  getThreadSize,
  columnDropdownRef,
  pageSizeDropdownRef,
  docStates,
  caseIssues,
  docIssues,
  issueFilter,
  onIssueClick
}: DocumentTableProps) {
  function SortIcon({
    column
  }: {
    column: SortColumn;
  }) {
    if (sortColumn !== column) {
      return <ChevronsUpDownIcon className="w-3 h-3 ml-1 opacity-40" data-id="element-213" />;
    }
    return sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 ml-1 text-amber" data-id="element-214" /> : <ChevronDownIcon className="w-3 h-3 ml-1 text-amber" data-id="element-215" />;
  }
  return <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${selectedDoc ? 'pr-[480px]' : ''}`} data-id="element-216">
      {/* Table Header */}
      <div className="grid gap-2 px-6 py-2.5 border-b border-navy-lighter bg-navy text-xs font-semibold text-gray-400 uppercase tracking-wider shrink-0 select-none" style={{
      gridTemplateColumns: gridTemplate
    }} data-id="element-217">
        {isColumnVisible('date') && <button onClick={() => handleSort('date')} className={`flex items-center text-left transition-colors hover:text-gray-200 ${sortColumn === 'date' ? 'text-amber' : ''}`} data-id="element-218">
            Date
            <SortIcon column="date" data-id="element-219" />
          </button>}
        {isColumnVisible('type') && <button onClick={() => handleSort('type')} className={`flex items-center text-left transition-colors hover:text-gray-200 ${sortColumn === 'type' ? 'text-amber' : ''}`} data-id="element-220">
            Type
            <SortIcon column="type" data-id="element-221" />
          </button>}
        {isColumnVisible('subject') && <button onClick={() => handleSort('subject')} className={`flex items-center text-left transition-colors hover:text-gray-200 ${sortColumn === 'subject' ? 'text-amber' : ''}`} data-id="element-222">
            Subject
            <SortIcon column="subject" data-id="element-223" />
          </button>}
        {isColumnVisible('summary') && <div data-id="element-224">AI Summary</div>}
        {isColumnVisible('sender') && <button onClick={() => handleSort('sender')} className={`flex items-center text-left transition-colors hover:text-gray-200 ${sortColumn === 'sender' ? 'text-amber' : ''}`} data-id="element-225">
            Sender
            <SortIcon column="sender" data-id="element-226" />
          </button>}
        {isColumnVisible('status') && <div data-id="element-227">Status</div>}
        {isColumnVisible('thread') && <div className="text-center" title="Email thread" data-id="element-228">
            <MailIcon className="w-3 h-3 mx-auto opacity-40" data-id="element-229" />
          </div>}
        {isColumnVisible('action') && <div className="flex justify-end" data-id="element-230">
            <ExternalLinkIcon className="w-3 h-3 opacity-40" data-id="element-231" />
          </div>}
        {isColumnVisible('id') && <button onClick={() => handleSort('id')} className={`flex items-center text-left transition-colors hover:text-gray-200 ${sortColumn === 'id' ? 'text-amber' : ''}`} data-id="element-232">
            Bates #<SortIcon column="id" data-id="element-233" />
          </button>}

        {/* Gear icon — column settings */}
        <div className="relative flex justify-end" ref={columnDropdownRef} data-id="element-234">
          <button onClick={() => setColumnDropdownOpen(!columnDropdownOpen)} className={`p-0.5 rounded transition-colors ${columnDropdownOpen ? 'text-amber' : 'text-gray-500 hover:text-gray-300'}`} title="Toggle columns" data-id="element-235">
            <Settings2Icon className="w-3.5 h-3.5" data-id="element-236" />
          </button>
          {columnDropdownOpen && <div className="absolute top-full right-0 mt-1 w-48 bg-navy border border-navy-lighter rounded-lg shadow-xl z-50 py-2" data-id="element-237">
              <div className="px-3 py-1.5 text-[10px] text-gray-500 uppercase tracking-wider font-semibold" data-id="element-238">
                Visible Columns
              </div>
              {TABLE_COLUMNS.map(col => {
            const visible = visibleColumns.has(col.id);
            return <button key={col.id} onClick={() => toggleColumn(col.id)} disabled={col.required} className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-xs transition-colors ${col.required ? 'opacity-40 cursor-not-allowed' : 'hover:bg-navy-hover'}`} data-id="element-239">
                    <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${visible ? 'bg-amber border-amber' : 'border-navy-lighter bg-navy'}`} data-id="element-240">
                      {visible && <CheckIcon className="w-2.5 h-2.5 text-navy" data-id="element-241" />}
                    </div>
                    <span className={visible ? 'text-white' : 'text-gray-400'} data-id="element-242">
                      {col.label}
                    </span>
                    {col.required && <span className="text-[9px] text-gray-500 ml-auto" data-id="element-243">
                        Required
                      </span>}
                  </button>;
          })}
            </div>}
        </div>
      </div>

      {/* Table Body */}
      <div className="flex-1 overflow-auto" data-id="element-244">
        {paginatedRows.length === 0 ? <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-245">
            <SearchIcon className="w-10 h-10 text-gray-600 mb-3" data-id="element-246" />
            <p className="text-sm text-gray-400" data-id="element-247">
              No documents match your filters
            </p>
            <button onClick={clearAllFilters} className="mt-3 text-sm text-amber hover:text-amber-hover transition-colors" data-id="element-248">
              Clear all filters
            </button>
          </div> : paginatedRows.map(({
        doc,
        isChild,
        isTerminal
      }) => {
        const isSelected = selectedDoc?.id === doc.id;
        const isThreadParent = doc.threadId && doc.threadPosition === 1;
        const threadExpanded = doc.threadId ? expandedThreads.has(doc.threadId) : false;
        const threadSize = doc.threadId ? getThreadSize(doc.threadId) : 0;
        const state = docStates[doc.id] || DEFAULT_REVIEW_STATE;
        const isPrivileged = state.privileged;
        const isReviewed = state.reviewed;
        const currentDocIssues = docIssues[doc.id] || [];
        const activeIssues = currentDocIssues.map(id => caseIssues.find(i => i.id === id)).filter(Boolean) as CaseIssue[];
        return <div key={doc.id} className={`
                  border-b border-navy-lighter transition-colors cursor-pointer
                  ${isChild ? 'ml-5 border-l border-l-navy-lighter' : ''}
                  ${isChild && !isPrivileged ? 'bg-[#0C1825]' : ''}
                  ${isTerminal && !isSelected && !isPrivileged ? 'border-l-2 border-l-amber' : ''}
                  ${isPrivileged && !isSelected ? 'bg-red-500/[0.08] border-l-[3px] border-l-red-500 shadow-[inset_0_0_30px_rgba(239,68,68,0.06)]' : ''}
                  ${isSelected && isPrivileged ? 'bg-red-500/[0.12] border-l-[3px] border-l-red-500' : ''}
                  ${isSelected && !isPrivileged ? 'bg-amber/[0.12] border-l-[3px] border-l-amber shadow-[inset_0_0_30px_rgba(245,158,11,0.06)]' : ''}
                  ${!isSelected && !isChild && !isPrivileged ? 'hover:bg-navy-hover' : ''}
                  ${!isSelected && isChild && !isPrivileged ? 'hover:bg-[#132838]' : ''}
                  ${!isSelected && isPrivileged ? 'hover:bg-red-500/[0.14]' : ''}
                `} onClick={() => {
          setHoveredDoc(null);
          setSelectedDoc(doc);
        }} onMouseEnter={e => handleRowMouseEnter(doc, e)} onMouseLeave={handleRowMouseLeave} data-id="element-249">
                {/* Main grid row */}
                <div className={`grid gap-2 items-center text-sm ${isChild ? 'pl-12 pr-6 py-2' : 'px-6 py-3'}`} style={{
            gridTemplateColumns: gridTemplate
          }} data-id="element-250">
                  {/* Date */}
                  {isColumnVisible('date') && <div className={`text-xs ${isPrivileged ? 'text-red-400' : 'text-gray-300'}`} data-id="element-251">
                      {doc.date}
                    </div>}

                  {/* Type */}
                  {isColumnVisible('type') && <div data-id="element-252">
                      <Badge variant={getTypeBadgeVariant(doc.type)} data-id="element-253">
                        {doc.type}
                      </Badge>
                    </div>}

                  {/* Subject */}
                  {isColumnVisible('subject') && <div className="flex items-center min-w-0" data-id="element-254">
                      {isChild && <span className={`mr-1 shrink-0 ${isPrivileged ? 'text-red-400' : 'text-gray-500'}`} data-id="element-255">
                          ↳
                        </span>}
                      {isReviewed && <CheckCircleIcon className={`w-3.5 h-3.5 mr-1.5 shrink-0 ${isPrivileged ? 'text-red-400' : 'text-green-500'}`} title="Reviewed" data-id="element-256" />}
                      <div className={`text-sm font-medium truncate ${isPrivileged ? 'text-red-300' : 'text-white'}`} data-id="element-257">
                        {doc.subject}
                      </div>
                    </div>}

                  {/* AI Summary (short) */}
                  {isColumnVisible('summary') && <div className={`text-sm truncate pr-2 ${isPrivileged ? 'text-red-400/70' : 'text-gray-400'}`} data-id="element-258">
                      {doc.shortSummary || doc.summary}
                    </div>}

                  {/* Sender */}
                  {isColumnVisible('sender') && <div className={`text-xs truncate ${isPrivileged ? 'text-red-400' : 'text-gray-300'}`} data-id="element-259">
                      {doc.sender}
                    </div>}

                  {/* Status */}
                  {isColumnVisible('status') && <div data-id="element-260">
                      <Badge variant={getStatusBadgeVariant(doc.status)} data-id="element-261">
                        {doc.status}
                      </Badge>
                      {isTerminal && <Badge variant="amber" className="ml-1 text-[10px] px-1 py-0" data-id="element-262">
                          Terminal
                        </Badge>}
                    </div>}

                  {/* Thread indicator */}
                  {isColumnVisible('thread') && <div className="flex justify-center" data-id="element-263">
                      {isThreadParent && <button onClick={e => {
                e.stopPropagation();
                toggleThread(doc.threadId!);
              }} className="flex items-center gap-0.5 text-gray-400 hover:text-amber transition-colors p-1 rounded" title={`${threadSize} emails in thread`} data-id="element-264">
                          <ChevronRightIcon className={`w-3.5 h-3.5 transition-transform duration-150 ${threadExpanded ? 'rotate-90' : ''}`} data-id="element-265" />
                          <span className="text-xs font-medium" data-id="element-266">
                            {threadSize}
                          </span>
                        </button>}
                    </div>}

                  {/* Open in new tab */}
                  {isColumnVisible('action') && <div className="flex justify-end" data-id="element-267">
                      {!isChild && <button onClick={e => {
                e.stopPropagation();
                window.open(`/documents/${doc.id}`, '_blank');
              }} className="p-1.5 text-gray-500 hover:text-amber hover:bg-navy rounded transition-colors" title="Open document in new tab" data-id="element-268">
                          <ExternalLinkIcon className="w-4 h-4" data-id="element-269" />
                        </button>}
                    </div>}

                  {/* Bates # (far right) */}
                  {isColumnVisible('id') && <div className={`font-mono text-xs truncate ${isPrivileged ? 'text-red-400' : 'text-gray-500'}`} data-id="element-270">
                      {doc.id}
                    </div>}

                  {/* Spacer for gear column */}
                  <div data-id="element-271" />
                </div>

                {/* Issues second line */}
                {activeIssues.length > 0 && <div className={`flex gap-1.5 pb-2 ${isChild ? 'pl-12 pr-6' : 'px-6'}`} data-id="element-272">
                    {activeIssues.map(issue => {
              const isActiveFilter = issueFilter === issue.id;
              return <button key={issue.id} onClick={e => {
                e.stopPropagation();
                onIssueClick(issue.id);
              }} className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium transition-all hover:opacity-80 ${isActiveFilter ? 'ring-1 ring-offset-1 ring-offset-navy' : ''}`} style={{
                color: issue.color,
                backgroundColor: issue.color + '15',
                ...(isActiveFilter ? {
                  ringColor: issue.color
                } : {})
              }} title={`Filter by ${issue.label}`} data-id="element-273">
                          <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{
                  backgroundColor: issue.color
                }} data-id="element-274" />
                          {issue.label}
                        </button>;
            })}
                  </div>}
              </div>;
      })}
      </div>

      {/* Pagination Footer */}
      <div className="border-t border-navy-lighter bg-navy-light px-6 py-2.5 flex items-center justify-between text-xs text-gray-400 shrink-0" data-id="element-275">
        <span data-id="element-276">
          {totalFiltered === 0 ? 'No documents' : `Showing ${showingFrom}–${showingTo} of ${totalFiltered} documents`}
        </span>

        <div className="flex items-center gap-4" data-id="element-277">
          {/* Page size selector */}
          <div className="relative" ref={pageSizeDropdownRef} data-id="element-278">
            <button onClick={() => setPageSizeDropdownOpen(!pageSizeDropdownOpen)} className="flex items-center gap-1 px-2 py-1 bg-navy border border-navy-lighter rounded text-gray-300 hover:bg-navy-hover transition-colors" data-id="element-279">
              {pageSize} / page
              <ChevronDownIcon className="w-3 h-3 opacity-50" data-id="element-280" />
            </button>
            {pageSizeDropdownOpen && <div className="absolute bottom-full right-0 mb-1 w-24 bg-navy border border-navy-lighter rounded-lg shadow-xl z-50 py-1" data-id="element-281">
                {([25, 50, 100] as const).map(size => <button key={size} onClick={() => {
              setPageSize(size);
              setPageSizeDropdownOpen(false);
            }} className={`w-full px-3 py-1.5 text-left text-xs transition-colors ${pageSize === size ? 'text-amber bg-amber/5' : 'text-gray-300 hover:bg-navy-hover'}`} data-id="element-282">
                    {size}
                  </button>)}
              </div>}
          </div>

          {/* Prev / Next */}
          <div className="flex items-center gap-1" data-id="element-283">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safeCurrentPage <= 1} className="p-1 text-gray-400 hover:text-white rounded transition-colors disabled:opacity-25 disabled:cursor-not-allowed" data-id="element-284">
              <ChevronUpIcon className="w-4 h-4 -rotate-90" data-id="element-285" />
            </button>
            <span className="text-gray-300 min-w-[60px] text-center" data-id="element-286">
              Page {safeCurrentPage} of {totalPages}
            </span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safeCurrentPage >= totalPages} className="p-1 text-gray-400 hover:text-white rounded transition-colors disabled:opacity-25 disabled:cursor-not-allowed" data-id="element-287">
              <ChevronDownIcon className="w-4 h-4 -rotate-90" data-id="element-288" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}